import React, { useState, useEffect, useMemo } from 'react';
import { IoIosLogOut } from "react-icons/io";
import { useLocation } from 'react-router-dom';
import './Profile.css';
import { API_ENDPOINTS } from '../config/api';
import ResumeProfile from '../components/ResumeProfile';
import Referral from '../components/ui/Referral';
import { authFetch } from '../utils/authFetch';

function Profile({
  handleLogout,
  userEmail,
  userFirstName,  
  userLastName,
  storedResults = []
}) {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [searchLeft, setSearchLeft] = useState(0);
  const [searchCount, setSearchCount] = useState(0);
  const [planType, setPlanType] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);

  const formatRuntime = (totalSeconds) => {
    totalSeconds = Number(totalSeconds);
    if (totalSeconds < 60) {
      return `${totalSeconds.toFixed(2)} seconds`;
    } else if (totalSeconds < 3600) {
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = Math.floor(totalSeconds % 60);
      return `${minutes} minute${minutes !== 1 ? 's' : ''}${seconds > 0 ? ` ${seconds} second${seconds !== 1 ? 's' : ''}` : ''}`;
    } else if (totalSeconds < 86400) {
      const hours = Math.floor(totalSeconds / 3600);
      const remainder = totalSeconds % 3600;
      const minutes = Math.floor(remainder / 60);
      return `${hours} hour${hours !== 1 ? 's' : ''}${minutes > 0 ? ` ${minutes} minute${minutes !== 1 ? 's' : ''}` : ''}`;
    } else {
      const days = Math.floor(totalSeconds / 86400);
      const remainder = totalSeconds % 86400;
      const hours = Math.floor(remainder / 3600);
      return `${days} day${days !== 1 ? 's' : ''}${hours > 0 ? ` ${hours} hour${hours !== 1 ? 's' : ''}` : ''}`;
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');

    if (tab === 'overview' || tab === 'resume' || tab === 'referrals') {
      setActiveTab(tab);
    }
  }, [location.search]);

  useEffect(() => {
    if (!userEmail) return;

    const fetchUserInfo = async () => {
      try {
        const response = await authFetch(
          `${API_ENDPOINTS.GET_USER_INFO}?user_id=${encodeURIComponent(userEmail)}`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch user info");
        }

        const data = await response.json();
        let userInfo;

        // Case 1: backend already returns an object with Item
        if (data.Item) {
          userInfo = data.Item;
        } else {
          // Case 2: backend wraps data in body (string or object)
          let rawBody = data.body ?? data;

          if (typeof rawBody === "string") {
            // Old string format: clean it up then JSON.parse
            rawBody = rawBody.replace(/^"|"$/g, "");
            let jsonStr = rawBody.replace(/User information:\s*/, "");
            jsonStr = jsonStr.replace(/'/g, '"');
            jsonStr = jsonStr.replace(/Decimal\("(\d+)"\)/g, "$1");
            const parsed = JSON.parse(jsonStr);
            userInfo = parsed.Item ?? parsed;
          } else if (rawBody && typeof rawBody === "object") {
            // Already a JSON object
            userInfo = rawBody.Item ?? rawBody;
          } else {
            console.error("Unexpected user info format:", data);
            return;
          }
        }

        if (userInfo) {
          const maxSearches = Number(userInfo.MaxSearches) || 0;
          const currentSearchCount = Number(userInfo.CurrentSearchCount) || 0;

          setSearchCount(currentSearchCount);
          setSearchLeft(maxSearches - currentSearchCount);

          if (maxSearches === 3) {
            setPlanType("Free Tier / 3-URL Daily Search Plan");
          } else if (maxSearches === 10) {
            setPlanType("Casual Job Seeker / Daily Search Plan");
          } else if (maxSearches === 25) {
            setPlanType("Active Job Seeker / Hourly Search Plan");
          } else if (maxSearches >= 1000) {
            setPlanType("Owner Plan / Admin Access");
          } else {
            setPlanType("Custom Plan");
          }
        } else {
          console.error("User info missing 'Item' property");
          setSearchLeft(0);
        }
      } catch (error) {
        console.error("Error fetching user info in Profile:", error);
      }
    };

    fetchUserInfo();
    setTimeout(() => setIsLoaded(true), 100);
  }, [userEmail]);

  const {
    totalSearches,
    totalGreenPills,
    totalRedPills,
    totalOrangePills,
    totalComputeTime,
  } = useMemo(() => {
    let searchesCount = 0;
    let greenCount = 0;
    let redCount = 0;
    let orangeCount = 0;
    let runtimeSum = 0;

    storedResults.forEach(result => {
      searchesCount++;
      if (result.runtime) {
        const runtimeNumber = parseFloat(result.runtime);
        if (!isNaN(runtimeNumber)) {
          runtimeSum += runtimeNumber;
        }
      }
      result.keywords.forEach(kw => {
        if (kw.match_type === 'exact') {
          greenCount++;
        } else if (kw.match_type === 'not-found') {
          redCount++;
        } else if (kw.match_type === 'partial') {
          orangeCount++;
        }
      });
    });

    return {
      totalSearches: searchesCount,
      totalGreenPills: greenCount,
      totalRedPills: redCount,
      totalOrangePills: orangeCount,
      totalComputeTime: runtimeSum,
    };
  }, [storedResults]);

  const totalTimeSaved = totalComputeTime * 15;


  // FIXED: Always render tabs in same container structure for consistency
  if (activeTab === 'resume') {
    return (
      <div className="profile-container resume-mode">
        {/* KEEP SAME STRUCTURE: Tabs inside profile-content wrapper */}
        <div className="profile-content" style={{ background: 'transparent', boxShadow: 'none', padding: '30px 0 0 0' }}>
          <div className="profile-tabs">
            <button
              className={`profile-tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Account Overview
            </button>
            <button
              className={`profile-tab ${activeTab === 'resume' ? 'active' : ''}`}
              onClick={() => setActiveTab('resume')}
            >
              Resume Profile
            </button>
            <button
              className={`profile-tab ${activeTab === 'referrals' ? 'active' : ''}`}
              onClick={() => setActiveTab('referrals')}
            >
              Referrals & Rewards
            </button>
          </div>
        </div>

        {/* Resume Profile - Rendered outside but tabs stay consistent */}
        <div style={{ width: '100%', backgroundColor: 'var(--off-white)' }}>
          <ResumeProfile userEmail={userEmail} />
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-content">
        <div className="profile-tabs">
          <button
            className={`profile-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Account Overview
          </button>
          <button
            className={`profile-tab ${activeTab === 'resume' ? 'active' : ''}`}
            onClick={() => setActiveTab('resume')}
          >
            Resume Profile
          </button>
          <button
            className={`profile-tab ${activeTab === 'referrals' ? 'active' : ''}`}
            onClick={() => setActiveTab('referrals')}
          >
            Referrals & Rewards
          </button>
        </div>

        {activeTab === 'overview' && (
          <>
            <h2 className="profile-heading">Account Overview</h2>

            <div className="profile-details">
              <div className="profile-row">
                <span className="profile-label">First Name:</span>
                <span className="profile-value">{userFirstName}</span>
              </div>
              <div className="profile-row">
                <span className="profile-label">Last Name:</span>
                <span className="profile-value">{userLastName}</span>
              </div>
              <div className="profile-row">
                <span className="profile-label">Email:</span>
                <span className="profile-value">{userEmail}</span>
              </div>
              <div className="profile-row">
                <span className="profile-label">Total Searches:</span>
                <span className="profile-value">{searchCount}</span>
              </div>
              <div className="profile-row">
                <span className="profile-label">Jobs Found (Green pills):</span>
                <span className="profile-value">
                  <span className="pill green-pill">{totalGreenPills}</span>
                </span>
              </div>
              <div className="profile-row">
                <span className="profile-label">Jobs Not found (Red pills):</span>
                <span className="profile-value">
                  <span className="pill red-pill">{totalRedPills}</span>
                </span>
              </div>
              <div className="profile-row">
                <span className="profile-label">Jobs Partially found (Partial pills):</span>
                <span className="profile-value">
                  <span className="pill orange-pill">{totalOrangePills}</span>
                </span>
              </div>
              <div className="profile-row">
                <span className="profile-label">Search left:</span>
                <span className="profile-value">{searchLeft}</span>
              </div>
              <div className="profile-row">
                <span className="profile-label">Compute Time:</span>
                <span className="profile-value">{formatRuntime(totalComputeTime)}</span>
              </div>
              <div className="profile-row">
                <span className="profile-label">Time Saved:</span>
                <span className="profile-value">{formatRuntime(totalTimeSaved)}</span>
              </div>
              <div className="profile-row">
                <span className="profile-label">Current Plan:</span>
                <span className="profile-value">{planType}</span>
              </div>
              <div className="profile-row">
                <span className="profile-label">Subscription Management:</span>
                <span className="profile-value">
                  <a
                    href={process.env.REACT_APP_STRIPE_PORTAL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="manage-plan-button-link"
                  >
                    Manage Plan
                  </a>
                </span>
              </div>
            </div>

            <button
              className="logout-button"
              onClick={handleLogout}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                fontWeight: 'bold'
              }}
            >
              LOGOUT
              <IoIosLogOut
                style={{
                  marginLeft: '8px',
                  fontSize: '1.2em'
                }}
              />
            </button>

            <div className="profile-row" style={{ paddingTop: "32px", color: "grey", fontSize: 12 }}>
              <span className="profile-value" style={{ paddingRight: "8px", color: "grey" }}>Version</span>
              <span className="profile-value">{"1.2.1"}</span>
            </div>
          </>
        )}

        {activeTab === 'referrals' && (
          <div className="referral-tab-content">
            <Referral />
          </div>
        )}
      </div>
    </div>
  );
}

export default Profile;